import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { LOGO_URL, APP_NAME } from '../lib/constants';
import { Shield, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api, { ADMIN_TOKEN_KEY } from '../lib/api';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/admin/login', { email, password });
      // Use sessionStorage for admin tokens (more secure, expires on tab close)
      sessionStorage.setItem(ADMIN_TOKEN_KEY, response.data.access_token);
      toast.success('Admin login successful');
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid admin credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-slate-700 bg-slate-800/50 backdrop-blur">
        <CardHeader className="text-center pb-2">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src={LOGO_URL} alt={APP_NAME} className="h-10" />
            <Shield className="h-8 w-8 text-[#3B82F6]" />
          </div>
          <CardTitle className="text-2xl font-bold text-white font-['Manrope']">
            Admin Portal
          </CardTitle>
          <p className="text-slate-400 text-sm">
            Privicy Advisory Services - Internal Access
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                <AlertCircle className="h-4 w-4" />
                {error}
              </div>
            )}
            
            <div>
              <label className="text-sm text-slate-300 mb-1 block">Admin Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                required
                className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                data-testid="admin-email-input"
              />
            </div>
            
            <div>
              <label className="text-sm text-slate-300 mb-1 block">Password</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                data-testid="admin-password-input"
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white"
              disabled={loading}
              data-testid="admin-login-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Authenticating...
                </>
              ) : (
                <>
                  <Shield className="mr-2 h-4 w-4" />
                  Access Admin Dashboard
                </>
              )}
            </Button>
          </form>
          
          <div className="mt-6 pt-4 border-t border-slate-700 text-center">
            <a href="/" className="text-sm text-slate-400 hover:text-white">
              ← Back to main site
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;
